import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, Trash2, X } from "lucide-react";
import { useEffect, useState } from "react";
import { api, ApiError } from "../../shared/api/client";
import type {
  EmployeeSummary,
  MeetingAttendeeInput,
  MeetingDetail,
  PageResponse,
  ProjectMemberView,
} from "../../shared/types/api";
import { Button, Drawer, Field, InlineAlert, Input, Select, Textarea } from "../../shared/ui";
import { joinLocal, meetingEnd, splitLocal, todayLocal } from "./meetingVocabulary";

interface Guest {
  name: string;
  email: string;
}

/**
 * Schedules a meeting, or changes one that has no minutes yet.
 *
 * One day per meeting: a date and two times. The organiser is added by the server, so the list
 * here is everyone else. People on the project are offered first.
 */
export function ScheduleMeetingDrawer({
  projectId,
  meeting,
  open,
  onClose,
  onSaved,
}: {
  projectId: string;
  meeting?: MeetingDetail;
  open: boolean;
  onClose: () => void;
  onSaved: (saved: MeetingDetail) => void;
}) {
  const queryClient = useQueryClient();
  const [title, setTitle] = useState("");
  const [date, setDate] = useState("");
  const [startTime, setStartTime] = useState("10:00");
  const [endTime, setEndTime] = useState("11:00");
  const [location, setLocation] = useState("");
  const [agenda, setAgenda] = useState("");
  const [invited, setInvited] = useState<string[]>([]);
  const [guests, setGuests] = useState<Guest[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const members = useQuery({
    queryKey: ["project-members", projectId, "meeting-picker"],
    queryFn: () => api.get<ProjectMemberView[]>(`/api/v1/projects/${projectId}/members?asOf=${todayLocal()}`),
    enabled: open,
  });
  const employees = useQuery({
    queryKey: ["employees", "picker"],
    queryFn: () =>
      api.get<PageResponse<EmployeeSummary>>("/api/v1/employees?active=true&size=200&sort=fullName,asc"),
    enabled: open,
  });

  useEffect(() => {
    if (!open) {
      return;
    }
    if (meeting) {
      const start = splitLocal(meeting.startsAt);
      setTitle(meeting.title);
      setDate(start.date);
      setStartTime(start.time);
      setEndTime(splitLocal(meeting.endsAt).time);
      setLocation(meeting.location ?? "");
      setAgenda(meeting.agenda ?? "");
      setInvited(
        meeting.attendees
          .filter((a) => !a.guest && a.employeeId && a.employeeId !== meeting.organiser.id)
          .map((a) => a.employeeId!),
      );
      setGuests(meeting.attendees.filter((a) => a.guest).map((a) => ({ name: a.name, email: a.email ?? "" })));
    } else {
      setTitle("");
      setDate(todayLocal());
      setStartTime("10:00");
      setEndTime("11:00");
      setLocation("");
      setAgenda("");
      setInvited([]);
      setGuests([]);
    }
    setError(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const people = employees.data?.content ?? [];
  const memberIds = new Set((members.data ?? []).map((m) => m.employeeId));
  const available = people.filter((p) => !invited.includes(p.id) && p.id !== meeting?.organiser.id);
  const fromProject = available.filter((p) => memberIds.has(p.id));
  const everyoneElse = available.filter((p) => !memberIds.has(p.id));
  const nameOf = (id: string) => people.find((p) => p.id === id)?.fullName ?? "Someone";

  const startsAt = joinLocal(date, startTime);
  // A meeting can run past midnight: an end time earlier than the start means the end is the
  // next local day, not "before it starts".
  const endsAt = meetingEnd(date, startTime, endTime);
  const timesValid = startsAt !== null && endsAt !== null && Date.parse(endsAt) > Date.parse(startsAt);
  const guestsValid = guests.every((g) => g.name.trim() && g.email.trim());
  const canSubmit = title.trim() !== "" && timesValid && guestsValid;

  const submit = async () => {
    setSaving(true);
    setError(null);
    const attendees: MeetingAttendeeInput[] = [
      ...invited.map((employeeId) => ({ employeeId, guestName: null, guestEmail: null })),
      ...guests.map((g) => ({ employeeId: null, guestName: g.name.trim(), guestEmail: g.email.trim() })),
    ];
    const body = {
      title: title.trim(),
      agenda: agenda.trim() || null,
      location: location.trim() || null,
      startsAt,
      endsAt,
      attendees,
      version: meeting?.version ?? null,
    };
    try {
      const saved = meeting
        ? await api.put<MeetingDetail>(`/api/v1/projects/${projectId}/meetings/${meeting.id}`, body)
        : await api.post<MeetingDetail>(`/api/v1/projects/${projectId}/meetings`, body);
      void queryClient.invalidateQueries({ queryKey: ["meetings", projectId] });
      onSaved(saved);
      onClose();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : "Could not save the meeting.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Drawer
      open={open}
      onClose={onClose}
      width="lg"
      title={meeting ? "Change meeting" : "Schedule meeting"}
      subtitle="Everyone with an account is told. Guests are recorded on the minutes."
      footer={
        <div className="flex justify-between gap-2">
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={() => void submit()} disabled={!canSubmit} loading={saving}>
            {meeting ? "Save changes" : "Schedule"}
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        {error && <InlineAlert tone="error">{error}</InlineAlert>}
        <Field label="Title" required>
          <Input value={title} maxLength={200} onChange={(e) => setTitle(e.target.value)} />
        </Field>
        <div className="grid grid-cols-3 gap-3">
          <Field label="Date" required>
            <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
          </Field>
          <Field label="Starts" required>
            <Input type="time" value={startTime} onChange={(e) => setStartTime(e.target.value)} />
          </Field>
          <Field label="Ends" required error={startsAt && endsAt && !timesValid ? "Ends before it starts." : undefined}>
            <Input type="time" value={endTime} onChange={(e) => setEndTime(e.target.value)} />
          </Field>
        </div>
        <Field label="Where" hint="A room, or the meeting link.">
          <Input value={location} maxLength={500} onChange={(e) => setLocation(e.target.value)} />
        </Field>
        <Field label="Agenda">
          <Textarea rows={4} value={agenda} onChange={(e) => setAgenda(e.target.value)} />
        </Field>

        <Field label="People" hint="You are on the list as the organiser.">
          <Select
            value=""
            onChange={(e) => {
              const id = e.target.value;
              if (id) {
                setInvited((current) => [...current, id]);
              }
            }}
          >
            <option value="">Add a person…</option>
            {fromProject.length > 0 && (
              <optgroup label="On this project">
                {fromProject.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.fullName} — {p.designation}
                  </option>
                ))}
              </optgroup>
            )}
            {everyoneElse.length > 0 && (
              <optgroup label="Everyone else">
                {everyoneElse.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.fullName} — {p.designation}
                  </option>
                ))}
              </optgroup>
            )}
          </Select>
        </Field>
        {invited.length > 0 && (
          <ul className="flex flex-wrap gap-2">
            {invited.map((id) => (
              <li key={id} className="inline-flex items-center gap-1 rounded-full bg-ink-100 px-3 py-1 text-sm">
                {nameOf(id)}
                <button
                  type="button"
                  aria-label={`Remove ${nameOf(id)}`}
                  className="text-ink-400 hover:text-ink-800"
                  onClick={() => setInvited((current) => current.filter((x) => x !== id))}
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </li>
            ))}
          </ul>
        )}

        <div className="space-y-2">
          {guests.map((guest, index) => (
            <div key={index} className="flex items-center gap-2">
              <Input
                placeholder="Guest name"
                value={guest.name}
                onChange={(e) =>
                  setGuests((current) => current.map((g, i) => (i === index ? { ...g, name: e.target.value } : g)))
                }
              />
              <Input
                type="email"
                placeholder="Email"
                value={guest.email}
                onChange={(e) =>
                  setGuests((current) => current.map((g, i) => (i === index ? { ...g, email: e.target.value } : g)))
                }
              />
              <Button
                variant="ghost"
                size="sm"
                title="Remove guest"
                onClick={() => setGuests((current) => current.filter((_, i) => i !== index))}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          ))}
          <Button
            variant="ghost"
            size="sm"
            icon={<Plus className="h-4 w-4" />}
            onClick={() => setGuests((current) => [...current, { name: "", email: "" }])}
          >
            Add a guest
          </Button>
        </div>
      </div>
    </Drawer>
  );
}
